/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-external-request-patterns, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { action, computed } from '@ember/object';
import { onAttachmentUpload } from '@linc-technologies/linced-common/utils/uppy';
import PageType from 'linced-app/enums/page-type';

export default class Screenshot extends Component {
  // Services
  @service
  paperToaster;

  @service
  session;

  @service
  store;

  @service
  uppy;

  PageType = PageType;
  @tracked
  attachments = [];
  @tracked
  editingPost = false;
  @tracked
  loading = false;
  @tracked
  saveIsDisabled = false;
  learner = null;
  postBody = '';
  loadingPageId = null;
  @tracked
  selectedPageType = PageType.SCHOOL;
  @tracked _selectedPerson = null;

  @computed('session.person', '_selectedPerson')
  get selectedPerson() {
    if (this._selectedPerson) {
      return this._selectedPerson;
    }
    return this.get('session.person');
  }

  @computed('type')
  get filteredPages() {
    return this.store
      .peekAll('posts/page')
      .filter((page) => page.type === PageType.SCHOOL)
      .filter((page) => page.visibleInMenu === true);
  }

  @computed('type')
  get learnerPages() {
    return this.store
      .peekAll('posts/page')
      .filter(
        (page) =>
          page.get('type') === PageType.STUDENT &&
          page.get('visibleInMenu') === true &&
          page.get('hasPosts') === true,
      );
  }

  @tracked _page = null;

  @tracked
  showingProgress;

  @computed('filteredPages', '_page')
  get page() {
    if (this._page) {
      return this._page;
    }
    return this.filteredPages[0] || null; // Return null if no pages match
  }

  async uploadFile(file) {
    if (isEmpty(file)) {
      return;
    }

    this.showingProgress = true;

    this.uppy.setupAttachments((file, upload, mediaType) => {
      let attachment = onAttachmentUpload(
        this.store,
        2,
        this.get('session.school'),
        this.get('session.person'),
        file,
        upload,
        mediaType,
      );
      this.attachments = [...this.attachments, attachment];
    });

    await this.uppy.uploadScreenshot(file);

    this.showingProgress = false;
    this.saveIsDisabled = false;
    this.loading = false;
  }

  @action
  noop() {}

  @action
  captureScreenshotAndAttach(divSelector, page) {
    this.showingProgress = true;
    this.saveIsDisabled = true;
    this.editingPost = true;
    this._performScreenshotCapture(divSelector, page);
    return false; // Prevent event propagation
  }

  @action
  closeEditPost() {
    this.editingPost = false;
    this.attachments = [];
  }

  @action
  refresh() {}

  async _performScreenshotCapture(divSelector, page) {
    this.showingProgress = true;
    this.attachments = [];
    this._page = page;
    this.selectedPageType = page.type;
    if (this.learner) {
      this._selectedPerson = this.learner;
    }
    const targetDiv = document.querySelector(divSelector);
    try {
      if (!targetDiv) {
        throw new Error('Target div not found');
      }

      // Add the "screenshot-mode" class to adjust styles for the screenshot
      targetDiv.classList.add('screenshot-mode');
      this.loading = true;
      // Generate the screenshot. html2canvas is large, so it loads on first use.
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(targetDiv, {
        useCORS: true,
        backgroundColor: null,
      });
      const dataUrl = canvas.toDataURL('image/png');
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      const screenshotFile = new File([blob], 'screenshot.png', {
        type: 'image/png',
      });

      // Remove the "screenshot-mode" class after the screenshot
      targetDiv.classList.remove('screenshot-mode');

      // Programmatically trigger the file upload
      await this.uploadFile(screenshotFile);
    } catch (error) {
      // Ensure the "screenshot-mode" class is removed even if there's an error
      if (targetDiv) {
        targetDiv.classList.remove('screenshot-mode');
      }

      this.loading = false;
      this.paperToaster.show(`Failed to capture screenshot: ${error}`, {
        duration: 4000,
        position: 'top right',
      });
    }
  }
}
